import {createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

import {AUDIT_LOG_EMPTY_CELL_VALUE} from 'audit/AuditConstants';
import getTestProps from 'test/lib/cypress/getTestProps';

import parsePythonReprToJson from './parsePythonReprToJson';

interface JsonFormattedCellValueProps {
  value: string | undefined;
}

function looksLikeStructuredData(value: string): boolean {
  const firstChar = value.trimStart()[0];
  return firstChar === '{' || firstChar === '[';
}

/**
 * If value is a JSON or Python repr object/array string, returns it pretty-printed
 * with 2-space indentation. Tries JSON.parse first, then falls back to Python repr
 * normalization (handles None, True, False, single quotes).
 * Otherwise returns value unchanged.
 */
function tryFormatJson(value: string): {formatted: string; isJson: boolean} {
  if (!looksLikeStructuredData(value)) {
    return {formatted: value, isJson: false};
  }

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object' && parsed != null) {
      return {formatted: JSON.stringify(parsed, null, 2), isJson: true};
    }
  } catch (e: unknown) {
    if (!(e instanceof SyntaxError)) {
      throw e;
    }
  }

  const parsed = parsePythonReprToJson(value);
  if (parsed != null) {
    return {formatted: JSON.stringify(parsed, null, 2), isJson: true};
  }

  return {formatted: value, isJson: false};
}

export default function JsonFormattedCellValue({value}: JsonFormattedCellValueProps): JSX.Element {
  const {classes, cx} = useStyles();
  const {formatted, isJson} = useMemo(
    () => (value ? tryFormatJson(value) : {formatted: value, isJson: false}),
    [value]
  );

  if (isJson) {
    return (
      <div {...getTestProps({component: 'JsonFormattedCellValue', element: 'root'})}>
        <pre
          className={cx(classes.cellValueWrapping, classes.jsonPre)}
          {...getTestProps({component: 'JsonFormattedCellValue', element: 'pre'})}
        >
          {formatted}
        </pre>
      </div>
    );
  }

  if (!formatted) {
    return (
      <div {...getTestProps({component: 'JsonFormattedCellValue', element: 'root'})}>
        {AUDIT_LOG_EMPTY_CELL_VALUE}
      </div>
    );
  }

  return (
    <div {...getTestProps({component: 'JsonFormattedCellValue', element: 'root'})}>
      <div
        className={classes.cellValueWrapping}
        {...getTestProps({component: 'JsonFormattedCellValue', element: 'plainText'})}
      >
        {formatted}
      </div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  // Shared containment for any Old/New value cell: preserve newlines, wrap long lines, and scroll
  // within a fixed-height box so a single row (e.g. a long routine prompt) can't dominate the table.
  cellValueWrapping: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere' as const,
    maxHeight: 200,
    overflow: 'auto',
    lineHeight: 1.45,
  },
  // Box chrome for pretty-printed JSON values; composed with cellValueWrapping via cx.
  jsonPre: {
    fontFamily: theme.fontFamilyMonospace,
    fontSize: theme.fontSizes.md,
    backgroundColor: theme.colors.gray[0],
    padding: theme.spacing.xs,
    borderRadius: theme.radius.sm,
    margin: 0,
  },
}));
